import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { getUserData, logout } from './services/authService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { hasAccess, isSubscriptionExpired } from './utils/accessControl';
import './App.css';
import { trackPageView } from './services/analyticsService';
import { logSessionStart } from './services/activityService';

const LoginRegister = lazy(() => import('./components/LoginRegister'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const MainPage = lazy(() => import('./pages/MainPage'));
const DermokozmetikTakip = lazy(() => import('./components/DermokozmetikTakip'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const Ayarlar = lazy(() => import('./components/Ayarlar'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const PaymentSecurityPage = lazy(() => import('./components/PaymentSecurityPage'));
const DistanceSalesPage = lazy(() => import('./components/DistanceSalesPage'));
const RefundPolicyPage = lazy(() => import('./components/RefundPolicyPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const PreInformationPage = lazy(() => import('./components/PreInformationPage'));
const MembershipTermsPage = lazy(() => import('./components/MembershipTermsPage'));
const DataDeletionPage = lazy(() => import('./components/DataDeletionPage'));

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Yükleniyor...</p>
      </div>
    </div>
  );
}

// Router içindeki tüm logic'i içeren wrapper component
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('user');

  // Admin kontrolü fonksiyonu
  const isAdmin = (userData: any) => {
    return userData?.isAdmin === true;
  };

  // Mevcut kullanıcı verilerini getir
  const getCurrentUserData = async () => {
    const user = auth.currentUser;
    if (user) {
      return await getUserData(user.uid);
    }
    return null;
  };

  useEffect(() => {
    let mounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) {
        return;
      }

      if (user) {
        if (!userData) {
          try {
            const firestoreUserData = await getCurrentUserData();
            
            if (!mounted) {
              return;
            }
            
            if (!firestoreUserData) {
              console.error('❌ Firestore kullanıcı verisi bulunamadı');
              await logout();
              if (mounted) {
                setIsLoggedIn(false);
                setUserData(null);
                setIsLoading(false);
              }
              return;
            }

            // Askıya alınan kullanıcılar giriş yapamaz (admin hariç)
            if (firestoreUserData.isActive === false && !firestoreUserData.isAdmin) {
              console.warn('⚠️ Askıya alınmış hesap giriş denemesi:', user.email);
              await logout();
              sessionStorage.setItem('login_error', 'Hesabınız askıya alınmıştır. Lütfen yönetici ile iletişime geçin: eczaneanalizapp@gmail.com');
              if (mounted) {
                setIsLoggedIn(false);
                setUserData(null);
                setIsLoading(false);
                navigate('/login', { replace: true });
              }
              return;
            }

            if (mounted) {
              setUserData(firestoreUserData);
              setIsLoggedIn(true);
              if (isAdmin(firestoreUserData)) {
                setUserRole('admin');
              } else {
                setUserRole('user');
              }
            }
          } catch (error) {
            console.error('❌ Firestore kullanıcı verisi çekme hatası:', error);
            await logout();
            if (mounted) {
              setIsLoggedIn(false);
              setUserData(null);
            }
          }
        } else {
            if (mounted) {
              setIsLoggedIn(true);
              if (isAdmin(userData)) {
                setUserRole('admin');
              } else {
                setUserRole('user');
              }
            }
        }
      } else {
        if (mounted) {
          setIsLoggedIn(false);
          setUserData(null);
          setUserRole('user');
        }
      }
      
      if (mounted) {
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    trackPageView({ path: location.pathname, title: document.title });
  }, [location.pathname]);

  // Kullanıcı dokümanını canlı dinle: admin panelindeki trial uzatma gibi
  // değişiklikler kullanıcı ekranına anlık yansısın.
  useEffect(() => {
    const currentUid = auth.currentUser?.uid;
    if (!isLoggedIn || !currentUid) return;

    const userRef = doc(db, 'users', currentUid);
    const unsubscribeUserDoc = onSnapshot(userRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const latest = snapshot.data();
      setUserData((prev: any) => {
        const normalize = (v: any) => {
          if (v === null || v === undefined) return v;
          if (v instanceof Date) return v.getTime();
          if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate().getTime();
          return v;
        };

        // Arama/aktivite logları `updatedAt` ve `lastActivityAt` gibi alanları sıkça değiştiriyor.
        // Bu “gürültü” state update -> re-render zinciri UI'yi sıfırlıyor.
        // Yalnızca UI'yi etkileyen alanlar değiştiğinde setUserData çağırıyoruz.
        const meaningfulKeys = [
          'trialStartDate',
          'trialEndDate',
          'hasActiveSubscription',
          'currentPeriodEnd',
          'subscriptionStatus',
          'isAdmin',
          'isActive',
          'isApproved',
          'createdAt',
          'arandi',
          'eczaneNotu',
          'eczaneAdi',
          'name',
          'il',
          'ilce',
          'adres',
          'telefon',
          'glnNumarasi',
          'trialExtendedAt',
        ];

        const prevObj = prev || {};
        const shouldUpdate = meaningfulKeys.some((k) => normalize(prevObj?.[k]) !== normalize((latest as any)?.[k]));

        if (!shouldUpdate) return prev;

        return {
          ...(prev || {}),
          ...latest,
          uid: currentUid
        };
      });
    });

    return () => unsubscribeUserDoc();
  }, [isLoggedIn]);

  // Session başlangıcı logla (oturum başına 1 kez)
  useEffect(() => {
    if (!isLoggedIn || !userData || isLoading) return;
    if (sessionStorage.getItem('activity_session_logged')) return;
    sessionStorage.setItem('activity_session_logged', '1');
    logSessionStart();
  }, [isLoggedIn, userData, isLoading]);

  // Süresi dolmuş premium (IBAN/dekont) — Firestore'da pasife çek
  useEffect(() => {
    if (!isLoggedIn || !userData?.uid || isLoading || userData.isAdmin) return;
    if (!isSubscriptionExpired(userData)) return;

    updateDoc(doc(db, 'users', userData.uid), {
      hasActiveSubscription: false,
      subscriptionStatus: 'expired',
      updatedAt: new Date()
    }).catch((err) => console.error('Premium süresi dolumu guncellenemedi:', err));
  }, [isLoggedIn, userData, isLoading]);

  const handleLogin = (user: any) => {
    // onAuthStateChanged listener'ı zaten state'i güncelleyecek
    // Burada sadece yönlendirme yapıyoruz
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('activity_session_logged');
      await logout();
      setIsLoggedIn(false);
      setUserData(null);
      setUserRole('user');
      navigate('/');
    } catch (error) {
      console.error('❌ Çıkış hatası:', error);
    }
  };

  if (isLoading) {
    return <RouteLoadingFallback />;
  }

  // Protected Route Component — giriş zorunlu; özellik erişimi sayfa içinde kontrol edilir
  const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
    if (!isLoggedIn) {
      return <LandingPage />;
    }
    return children;
  };

  const PremiumRoute = ({ children }: { children: React.ReactElement }) => {
    if (!isLoggedIn) {
      return <LandingPage />;
    }
    if (!hasAccess(userData)) {
      return <Navigate to="/ayarlar?tab=odeme" replace />;
    }
    return children;
  };

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
      {/* Public routes */}
      <Route path="/" element={!isLoggedIn ? <LandingPage /> : <Navigate to="/eczane-analiz" replace />} />
      <Route path="/login" element={<LoginRegister onLogin={handleLogin} />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/gizlilik-ve-guvenlik" element={<PaymentSecurityPage />} />
      <Route path="/on-bilgilendirme" element={<PreInformationPage />} />
      <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesPage />} />
      <Route path="/iptal-iade" element={<RefundPolicyPage />} />
      <Route path="/iletisim" element={<ContactPage />} />
      <Route path="/uyelik-sozlesmesi" element={<MembershipTermsPage />} />
      <Route path="/veri-silme" element={<DataDeletionPage />} />
      
      {/* Protected routes */}
      <Route 
        path="/eczane-analiz" 
        element={
          <ProtectedRoute>
            <MainPage userData={userData} handleLogout={handleLogout} setUserData={setUserData} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dermokozmetik-takip/*" 
        element={
          <PremiumRoute>
            <DermokozmetikTakip />
          </PremiumRoute>
        } 
      />
      <Route 
        path="/ayarlar" 
        element={
          <ProtectedRoute>
            <Ayarlar userData={userData} onUserDataUpdate={setUserData} />
          </ProtectedRoute>
        } 
      />
      {isLoggedIn && userData?.isAdmin && (
        <Route 
          path="/admin" 
          element={<AdminDashboard userData={userData} />} 
        />
      )}
      </Routes>
    </Suspense>
  );
};

// Ana App component'ı - sadece Router wrap ediyor
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
